import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilIcon, TrashIcon, KeyIcon, UserPlusIcon, ArrowUpTrayIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { adminAPI, importAPI } from '../utils/api';
import { notify } from '../utils/notifications';
import { useMutation } from '../hooks/useApi';

const MitraManagementModal = ({ onClose, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showImportForm, setShowImportForm] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    account_id: '',
    company_name: '',
    contact_person: '',
    address: '',
    current_bandwidth: ''
  });

  const { mutate: createUser, loading: createLoading } = useMutation(
    (userData) => adminAPI.createUser(userData),
    {
      onSuccess: () => {
        notify.success('User created successfully!');
        setShowCreateForm(false);
        setFormData({
          username: '', email: '', password: '', account_id: '', 
          company_name: '', contact_person: '', address: '', current_bandwidth: ''
        });
        fetchUsers();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        notify.error(error.response?.data?.message || 'Failed to create user');
      }
    }
  );

  const { mutate: updateUser, loading: updateLoading } = useMutation(
    ({ id, userData }) => adminAPI.updateUser(id, userData),
    {
      onSuccess: () => {
        notify.success('User updated successfully!');
        setEditingUser(null);
        setFormData({
          username: '', email: '', password: '', account_id: '', 
          company_name: '', contact_person: '', address: '', current_bandwidth: ''
        });
        fetchUsers();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        notify.error(error.response?.data?.message || 'Failed to update user');
      }
    }
  );

  const { mutate: deleteUser, loading: deleteLoading } = useMutation(
    (id) => adminAPI.deleteUser(id),
    {
      onSuccess: () => {
        notify.success('User deleted successfully!');
        fetchUsers();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        notify.error(error.response?.data?.message || 'Failed to delete user');
      }
    }
  );

  const { mutate: resetPassword, loading: resetLoading } = useMutation(
    ({ id, newPassword }) => adminAPI.resetUserPassword(id, { newPassword }),
    {
      onSuccess: () => {
        notify.success('Password reset successfully! User will be required to change password on next login.');
        fetchUsers();
      },
      onError: (error) => {
        notify.error(error.response?.data?.message || 'Failed to reset password');
      }
    }
  );

  const { mutate: importMitraData, loading: importLoading } = useMutation(
    (formData) => importAPI.importMitra(formData),
    {
      onSuccess: (response) => {
        notify.success(response.data.message);
        setShowImportForm(false);
        setImportFile(null);
        setImportErrors([]);
        fetchUsers();
        onSuccess && onSuccess();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          setImportErrors(errorData.errors);
          notify.error(`Import failed with ${errorData.errors.length} errors. Check details below.`);
        } else {
          notify.error(errorData?.message || 'Failed to import data');
        }
      }
    }
  );

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers({
        role: 'mitra',
        search: search || undefined,
        page: currentPage,
        limit: 10
      });
      setUsers(response.data.data.users);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      notify.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [search, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingUser) {
      const { password, ...updateData } = formData;
      updateUser({ id: editingUser.id, userData: updateData });
    } else {
      createUser(formData);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      account_id: user.account_id || '',
      company_name: user.company_name || '',
      contact_person: user.contact_person || '',
      address: user.address || '',
      current_bandwidth: user.current_bandwidth || ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      deleteUser(user.id);
    }
  };

  const handleResetPassword = (user) => {
    const newPassword = prompt(`Enter new password for ${user.username}:`);
    if (newPassword && newPassword.length >= 6) {
      resetPassword({ id: user.id, newPassword });
    } else if (newPassword !== null) {
      notify.error('Password must be at least 6 characters long');
    }
  };

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setFormData({
      username: '', email: '', password: '', account_id: '', 
      company_name: '', contact_person: '', address: '', current_bandwidth: ''
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        notify.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        e.target.value = '';
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        notify.error('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      
      setImportFile(file);
      setImportErrors([]);
    }
  };

  const handleImport = () => {
    if (!importFile) {
      notify.error('Please select a file to import');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);
    importMitraData(formData);
  };

  const handleDownloadTemplate = async (format) => {
    try {
      const response = await importAPI.downloadMitraTemplate(format);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mitra_template.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      notify.success(`Template downloaded successfully`);
    } catch (error) {
      notify.error('Failed to download template');
    }
  };

  const cancelImport = () => {
    setShowImportForm(false);
    setImportFile(null);
    setImportErrors([]);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Manage Mitra Users</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!showCreateForm && !showImportForm ? (
          <>
            <div className="mb-4 flex justify-between items-center">
              <div className="flex-1 max-w-sm">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="form-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowImportForm(true)}
                  className="btn-secondary flex items-center"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  Import CSV/Excel
                </button>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="btn-primary flex items-center"
                >
                  <UserPlusIcon className="h-4 w-4 mr-2" />
                  Add New User
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account Info
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {user.username}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm text-gray-900">
                                {user.company_name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.contact_person || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm text-gray-900">
                                ID: {user.account_id || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                BW: {user.current_bandwidth || 0} Mbps
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEdit(user)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit User"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(user)}
                                disabled={resetLoading}
                                className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                title="Reset Password"
                              >
                                <KeyIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(user)}
                                disabled={deleteLoading}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                title="Delete User"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="btn-secondary"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                        disabled={currentPage === pagination.pages}
                        className="btn-secondary"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{((currentPage - 1) * 10) + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(currentPage * 10, pagination.total)}
                          </span> of{' '}
                          <span className="font-medium">{pagination.total}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                            disabled={currentPage === pagination.pages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : showImportForm ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">Import Mitra Data</h4>
              <button onClick={cancelImport} className="btn-secondary">
                Cancel
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-blue-800 mb-2">Before importing:</h5>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Download the template file to see the required format</li>
                <li>• Supported formats: CSV, Excel (.xlsx, .xls)</li>
                <li>• Maximum file size: 5MB</li>
                <li>• Required fields: username, email, company_name, current_bandwidth, address</li>
                <li>• Default password will be 'client123' if not specified</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Download Template</label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleDownloadTemplate('csv')}
                    className="btn-secondary flex items-center"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Download CSV Template
                  </button>
                  <button
                    onClick={() => handleDownloadTemplate('xlsx')}
                    className="btn-secondary flex items-center"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Download Excel Template
                  </button>
                </div>
              </div>

              <div>
                <label className="form-label">Select File to Import</label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="form-input"
                />
                {importFile && (
                  <p className="text-sm text-gray-600 mt-1">
                    Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {importErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-red-800 mb-2">Import Errors:</h5>
                  <div className="max-h-48 overflow-y-auto">
                    <ul className="text-sm text-red-700 space-y-1">
                      {importErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button onClick={cancelImport} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={!importFile || importLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {importLoading ? 'Importing...' : 'Import Data'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h4>
              <button
                onClick={cancelForm}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="form-label">Password *</label>
                    <input
                      type="password"
                      className="form-input"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      minLength={6}
                    />
                  </div>
                )}
                <div>
                  <label className="form-label">Account ID</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.account_id}
                    onChange={(e) => setFormData({...formData, account_id: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Contact Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    pattern="[\+]?[0-9\s\-\(\)]{10,15}"
                    title="Please enter a valid phone number (10-15 digits, may include +, spaces, hyphens, or parentheses)"
                    placeholder="e.g., +62 812 3456 7890 or 08123456789"
                  />
                </div>
                <div>
                  <label className="form-label">Current Bandwidth (Mbps)</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input"
                    value={formData.current_bandwidth}
                    onChange={(e) => setFormData({...formData, current_bandwidth: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Address</label>
                <textarea
                  rows={3}
                  className="form-input"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || updateLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {(createLoading || updateLoading) ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default MitraManagementModal;